import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { FleetPage, Notice } from '../../../../shared/sso-workspace/fleet-page'
import { Pill } from '../../../../shared/sso-workspace/pill'
import { formatAgo, formatDateTime } from '../../../../shared/sso-workspace/format'
import { serverMessage } from '../../../../shared/error/server-message'
import { ErrorReference } from '../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../shared/error/use-error-line'
import { EmptyState } from '../../../../shared/empty-state/empty-state'
import { IconCourseLaunch, IconNotification } from '../../../../shared/icon/icon'
import { archive, isOffline, listInbox, markAllRead, markRead } from '../../../../api-new/state/notify'
import { categoryLabel } from '../../../../shared/notify/words'
import { useSelfNotifyAccess } from '../../../../shared/notify/self-access'
import { setUnread, useUnread } from './unread-store'
import './notifications.scss'

export const PAGE_SIZE = 50
export const PREFERENCES_PATH = '/org/notifications/preferences'

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'unread', label: 'Unread' },
  { key: 'archived', label: 'Archived' }
]

const EMPTY = {
  all: { title: 'No notifications yet', message: 'Courses shared with you, invitations and sign-in alerts appear here.' },
  unread: { title: "You're all caught up", message: 'Nothing unread.' },
  archived: { title: 'Nothing archived', message: 'Notifications you archive move here.' }
}

const OFFLINE = "We can't reach notifications right now. Check your connection, then try again."
const LOAD_FAILED = "Your notifications couldn't be loaded."
const ACTION_FAILED = "That couldn't be saved. Please try again."

// Where an item's link (C-5: always edxr://) goes in the admin: a course
// opens through the launch bridge, the one way into a course from the web.
// Nothing else maps to a page here, so nothing else is offered as a link.
export const linkHref = link => {
  if (!link || typeof link !== 'string') return null
  const m = /^edxr:\/\/content\?id=([1-9]\d{0,17})$/.exec(link.trim())
  return m ? `/launch/content/${m[1]}` : null
}

// The person's inbox in the active organization (C-4 /inbox): newest first,
// paged by cursor ("Show older"), with All / Unread / Archived, mark read,
// mark all as read and archive. It reads once and never polls by itself:
// the header bell polls the unread count (edxr-bg=1), and this page
// refetches its first page, as a background request, only when the bell
// sees a newer item (latestId moved).
export const InboxPage = () => {
  const access = useSelfNotifyAccess()
  const unread = useUnread()
  const [filter, setFilter] = useState('all')
  const [items, setItems] = useState([])
  const [cursor, setCursor] = useState(null)
  const [state, setState] = useState('loading') // loading | ready | offline | error
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, failure, setError] = useErrorLine()
  const [actionError, actionFailure, setActionError] = useErrorLine()
  const [busy, setBusy] = useState(null)
  const live = useRef(true)
  const filterRef = useRef(filter)
  filterRef.current = filter
  const itemsRef = useRef(items)
  itemsRef.current = items

  useEffect(() => {
    live.current = true
    return () => {
      live.current = false
    }
  }, [])

  const loadFirst = useCallback(async ({ background = false } = {}) => {
    const asked = filterRef.current
    if (!background) setState(s => (s === 'ready' ? s : 'loading'))
    try {
      const page = await listInbox({ filter: asked, limit: PAGE_SIZE, cursor: undefined }, { background })
      if (!live.current || filterRef.current !== asked) return
      setItems((page && page.items) || [])
      setCursor((page && page.cursor) || null)
      if (page && typeof page.unreadCount === 'number') setUnread({ count: page.unreadCount })
      setError(null)
      setState('ready')
    } catch (err) {
      if (!live.current || filterRef.current !== asked) return
      // A background refresh that fails keeps what is on screen.
      if (background) return
      if (isOffline(err)) {
        setError(null)
        setState('offline')
      } else {
        setError(serverMessage(err, LOAD_FAILED), err)
        setState('error')
      }
    }
  }, [setError])

  useEffect(() => {
    setItems([])
    setCursor(null)
    loadFirst()
  }, [filter, loadFirst])

  // Something new arrived (the bell's poll moved latestId): refetch the
  // first page quietly. The first value seen is the baseline.
  const seenLatest = useRef(undefined)
  useEffect(() => {
    if (seenLatest.current === undefined) {
      seenLatest.current = unread.latestId
      return
    }
    if (unread.latestId === seenLatest.current) return
    seenLatest.current = unread.latestId
    // Already on screen (the first count after the page loaded, or an
    // action's answer): nothing to fetch.
    if (!unread.latestId || itemsRef.current.some(i => i.id === unread.latestId)) return
    if (filterRef.current !== 'archived') loadFirst({ background: true })
  }, [unread.latestId, loadFirst])

  const loadMore = async () => {
    if (!cursor || loadingMore) return
    const asked = filter
    setLoadingMore(true)
    try {
      const page = await listInbox({ filter: asked, limit: PAGE_SIZE, cursor }, { background: false })
      if (!live.current || filterRef.current !== asked) return
      const seen = new Set(items.map(i => i.id))
      setItems(list => [...list, ...((page && page.items) || []).filter(i => !seen.has(i.id))])
      setCursor((page && page.cursor) || null)
      setActionError(null)
    } catch (err) {
      if (live.current) setActionError(isOffline(err) ? OFFLINE : serverMessage(err, "Older notifications couldn't be loaded."), isOffline(err) ? null : err)
    } finally {
      if (live.current) setLoadingMore(false)
    }
  }

  const act = async (key, call, apply) => {
    if (!access.canWrite || busy) return
    setBusy(key)
    setActionError(null)
    try {
      const next = await call()
      if (!live.current) return
      apply()
      if (next) setUnread(next)
    } catch (err) {
      if (live.current) setActionError(isOffline(err) ? OFFLINE : serverMessage(err, ACTION_FAILED), isOffline(err) ? null : err)
    } finally {
      if (live.current) setBusy(null)
    }
  }

  const now = () => new Date().toISOString()

  const onRead = item =>
    act(`read:${item.id}`, () => markRead(item.id), () =>
      setItems(list => (filter === 'unread' ? list.filter(i => i.id !== item.id) : list.map(i => (i.id === item.id ? { ...i, read: true, readAt: i.readAt || now() } : i)))))

  const onArchive = item =>
    act(`archive:${item.id}`, () => archive(item.id), () => setItems(list => list.filter(i => i.id !== item.id)))

  const onReadAll = () =>
    act('read-all', async () => (await markAllRead()) || { count: 0 }, () =>
      setItems(list => (filter === 'unread' ? [] : list.map(i => (i.read ? i : { ...i, read: true, readAt: now() })))))

  const count = unread.known ? unread.count : items.filter(i => !i.read).length

  return (
    <FleetPage
      title="Notifications"
      purpose="What EducationXR told you in this organization, newest first."
      testId="notify-inbox"
      actions={
        <>
          <button type="button" className="btn btn-sm sw-btn" disabled={!access.canWrite || count === 0 || busy === 'read-all'} onClick={onReadAll}>Mark all as read</button>
          <Link className="btn btn-sm sw-btn" to={PREFERENCES_PATH}>Preferences</Link>
        </>
      }>
      {access.reason && <Notice tone="warn" role="status">{access.reason}</Notice>}

      <div className="d-flex flex-wrap align-items-center gap-3 mb-2">
        <nav className="sw-tabs mb-0" role="tablist" aria-label="Show">
          {FILTERS.map(f =>
            <button key={f.key} type="button" role="tab" aria-selected={filter === f.key ? 'true' : 'false'}
              className={`btn btn-link${filter === f.key ? ' active' : ''}`} onClick={() => setFilter(f.key)}>
              {f.label}
            </button>)}
        </nav>
        {count > 0 && <span className="sw-hint">{`${count} unread`}</span>}
      </div>

      {actionError && <p className="text-danger small" role="alert">{actionError}</p>}
      {actionError && <ErrorReference error={actionFailure} className="mb-2" />}

      {state === 'loading' && <p className="sw-hint" role="status">Loading…</p>}

      {state === 'offline' &&
        <Notice tone="warn" role="alert">
          <p className="mb-2">{OFFLINE}</p>
          <button type="button" className="btn btn-sm sw-btn" onClick={() => loadFirst()}>Try again</button>
        </Notice>}

      {state === 'error' &&
        <div>
          <p className="text-danger small" role="alert">{error}</p>
          <ErrorReference error={failure} className="mb-2" />
          <button type="button" className="btn btn-sm sw-btn" onClick={() => loadFirst()}>Try again</button>
        </div>}

      {state === 'ready' && items.length === 0 &&
        <EmptyState Icon={IconNotification} title={EMPTY[filter].title} message={EMPTY[filter].message} />}

      {state === 'ready' && items.length > 0 &&
        <section className="sw-section">
          <ul className="nt-list" aria-label="Notifications">
            {items.map(item =>
              <InboxItem key={item.id} item={item} canWrite={access.canWrite} busy={busy}
                onRead={() => onRead(item)} onArchive={() => onArchive(item)} />)}
          </ul>
          {cursor &&
            <button type="button" className="btn btn-sm sw-btn mt-2" disabled={loadingMore} onClick={loadMore}>
              {loadingMore ? 'Loading…' : 'Show older'}
            </button>}
        </section>}
    </FleetPage>
  )
}

const InboxItem = ({ item, canWrite, busy, onRead, onArchive }) => {
  const href = linkHref(item.link)
  const unread = !item.read
  return (
    <li className={`nt-item${unread ? ' is-unread' : ' is-read'}`} data-testid={`inbox-item-${item.id}`}>
      <span className={`nt-dot${unread ? ' is-unread' : ''}`} aria-hidden="true" />
      <div className="nt-item-body">
        <div className="nt-item-title">{item.title}</div>
        {item.body && <p className="nt-item-text">{item.body}</p>}
        <div className="nt-item-meta sw-hint">
          <span title={formatDateTime(item.createdAt)}>{formatAgo(item.createdAt)}</span>
          {item.category && <span>{categoryLabel(item.category)}</span>}
          {item.count > 1 && <span>{`${item.count} updates`}</span>}
          {unread && <Pill tone="info">Unread</Pill>}
          {item.priority === 'critical' && <Pill tone="bad">Urgent</Pill>}
        </div>
      </div>
      <div className="nt-item-actions">
        {href && <Link className="btn btn-sm sw-btn-primary" to={href} onClick={() => unread && canWrite && onRead()}><IconCourseLaunch /> Launch</Link>}
        {unread && <button type="button" className="btn btn-sm sw-btn" disabled={!canWrite || busy === `read:${item.id}`} onClick={onRead}>Mark as read</button>}
        {!item.archived && <button type="button" className="btn btn-sm sw-btn" disabled={!canWrite || busy === `archive:${item.id}`} onClick={onArchive}>Archive</button>}
      </div>
    </li>
  )
}
